package com.kanati.admin.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.kanati.admin.state.MaterialThemePalette
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.dom.Text

@Composable
fun FieldRow(content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                flexDirection(org.jetbrains.compose.web.css.FlexDirection.Row)
                gap(12.px)
                property("flex-wrap", "wrap")
            }
        }
    ) {
        content()
    }
}

@Composable
fun TextField(
    theme: MaterialThemePalette,
    label: String,
    value: String,
    placeholder: String = "",
    inputType: InputType<String> = InputType.Text,
    inputTypeOverride: String? = null,
    step: String? = null,
    inputPaddingVerticalPx: Int = 14,
    inputPaddingHorizontalPx: Int = 16,
    inputFontSize: String = "0.98rem",
    minHeightPx: Int? = null,
    onValueChange: (String) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                gap(6.px)
                flex(1)
                property("min-width", "0")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.78rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                }
            }
        ) {
            Text(label)
        }
        Input(type = inputType) {
            value(value)
            placeholder(placeholder)
            inputTypeOverride?.let { attr("type", it) }
            step?.let { attr("step", it) }
            onInput { onValueChange(it.value) }
            onChange { onValueChange(it.value) }
            style {
                padding(inputPaddingVerticalPx.px, inputPaddingHorizontalPx.px)
                borderRadius(14.px)
                property("box-sizing", "border-box")
                property("width", "100%")
                minHeightPx?.let { property("min-height", "${it}px") }
                property("background-color", theme.surface)
                property("color", theme.onSurface)
                property("border", "1px solid ${theme.outline}")
                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                property("font-size", inputFontSize)
                property("font-weight", "500")
                property("letter-spacing", "-0.01em")
            }
        }
    }
}

@Composable
fun TextAreaField(
    theme: MaterialThemePalette,
    label: String,
    value: String,
    placeholder: String = "",
    rows: Int = 8,
    indentWithSpaces: Int? = null,
    labelAction: (@Composable () -> Unit)? = null,
    onValueChange: (String) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
                gap(6.px)
                flex(1)
                property("min-width", "0")
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                    property("justify-content", "space-between")
                    property("align-items", "center")
                    gap(10.px)
                }
            }
        ) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("color", theme.onSurfaceVariant)
                        property("font-size", "0.78rem")
                        property("font-weight", "700")
                        property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase")
                    }
                }
            ) {
                Text(label)
            }
            labelAction?.invoke()
        }
        TextArea(
            value = value,
            attrs = {
                placeholder(placeholder)
                attr("rows", rows.toString())
                indentWithSpaces?.let { attr("data-indent-spaces", it.toString()) }
                onInput { onValueChange(it.value) }
                style {
                    padding(14.px, 16.px)
                    borderRadius(14.px)
                    property("box-sizing", "border-box")
                    property("width", "100%")
                    property("min-height", "180px")
                    property("resize", "vertical")
                    property("background-color", theme.surface)
                    property("color", theme.onSurface)
                    property("border", "1px solid ${theme.outline}")
                    property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                    property("font-size", "0.94rem")
                    property("font-family", "'SFMono-Regular', 'JetBrains Mono', monospace")
                    property("line-height", "1.5")
                }
            },
        )
    }
}

@Composable
fun ActionButton(
    label: String,
    onClick: () -> Unit,
    theme: MaterialThemePalette,
    isDisabled: Boolean = false,
    isPrimary: Boolean = true,
) {
    var isPressed by remember(isDisabled) { mutableStateOf(false) }

    Button(
        attrs = {
            onClick { onClick() }
            if (isDisabled) disabled()
            onMouseDown { if (!isDisabled) isPressed = true }
            onMouseUp { isPressed = false }
            onMouseLeave { isPressed = false }
            onTouchStart { if (!isDisabled) isPressed = true }
            onTouchEnd { isPressed = false }
            onTouchCancel { isPressed = false }
            onBlur { isPressed = false }
            style {
                padding(12.px, 18.px)
                borderRadius(14.px)
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("width", "fit-content")
                property("min-width", "120px")
                property("align-self", "flex-start")
                property("cursor", if (isDisabled) "default" else "pointer")
                property("background-color", if (isPrimary) theme.primary else theme.surfaceContainerHighest)
                property("color", if (isPrimary) theme.onPrimary else theme.onSurface)
                property("border", "1px solid ${if (isPrimary) theme.primary else theme.outline}")
                property("font-size", "0.95rem")
                property("font-weight", "700")
                property("letter-spacing", "-0.01em")
                property(
                    "box-shadow",
                    if (isPressed && !isDisabled) {
                        "0 2px 8px ${theme.shadow}"
                    } else {
                        "0 8px 18px ${theme.shadow}"
                    }
                )
                property(
                    "transform",
                    if (isPressed && !isDisabled) "translateY(1px)" else "translateY(0)"
                )
                property("transition", "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease")
                property("will-change", "transform, box-shadow")
                property("touch-action", "manipulation")
                property("user-select", "none")
                property("-webkit-tap-highlight-color", "transparent")
                if (isDisabled) {
                    property("opacity", "0.65")
                }
            }
        }
    ) {
        Text(label)
    }
}
